import { AnimatePresence, motion } from 'framer-motion';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { tokens } from '@treams/treams-ui';

const BORDER_WIDTH = 1;

const HEIGHT_TRANSITION = { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const };
// Enter: slide in from beyond the right edge; exit: slide out the same way.
const SLIDE_TRANSITION = { duration: 0.3, ease: [0.22, 1, 0.36, 1] as const };
const OFFSCREEN = { x: '110%' };
const SURFACE_TRANSITION = { duration: 0.15, ease: 'easeInOut' as const };
const INSTANT = { duration: 0 };

// An exiting slot no longer receives props; AnimatePresence hands it `custom`
// (whether to slide) at the moment it leaves.
const SLOT_VARIANTS = {
	exit: (slide: boolean) =>
		slide ? { ...OFFSCREEN, transition: SLIDE_TRANSITION } : { opacity: 0, transition: INSTANT },
};

const Slot = styled(motion.div)`
	position: relative;
	width: 100%;
	box-sizing: border-box;
	overflow: hidden;
	border: ${BORDER_WIDTH}px solid transparent;
	border-radius: ${tokens.radius.md}px;
	background-color: transparent;
	box-shadow:
		0 2px 4px rgba(20, 16, 28, 0),
		0 1px 2px rgba(20, 16, 28, 0);
	transition:
		background-color 150ms ease-in-out,
		border-color 150ms ease-in-out,
		box-shadow 150ms ease-in-out;

	&:hover,
	&[data-raised='true'],
	&:has(:focus-visible) {
		background-color: ${tokens.surface.white};
		border-color: ${tokens.border.subtle};
		box-shadow: ${tokens.shadow.xs};
	}

	& + & {
		margin-top: ${tokens.gap.element}px;
	}
`;

/**
 * One sidebar slot: a person's row, or their card once it is open in the same
 * place. Its height follows the content, animated, so swapping the content
 * (row ↔ card) glides the list below it along. Slots are
 * listbox options.
 * @param props component props
 * @param props.userID the person shown, for lookups by the sidebar
 * @param props.contentKey changes crossfade the content
 * @param props.raised shown on a white surface, like a hovered row
 * @param props.slide slide in and out; off shows and hides the slot at once,
 * like plain filtered results. Exits read this from AnimatePresence `custom`.
 * @param props.onSettled the enter or height animation finished
 * @param props.onMouseEnter the pointer entered the slot
 * @param props.onMouseLeave the pointer left the slot
 * @param props.children the row or card
 */
export default function TalentReviewSidebarSlot(props: {
	userID: string;
	contentKey: string;
	raised: boolean;
	slide: boolean;
	onSettled?: () => void;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	children: ReactNode;
}) {
	const contentRef = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState<number>();

	useLayoutEffect(() => {
		const content = contentRef.current;
		if (!content) {
			return;
		}

		const measure = () => setHeight(content.getBoundingClientRect().height);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(content);
		return () => observer.disconnect();
	}, []);

	return (
		<Slot
			data-user-id={props.userID}
			data-raised={props.raised}
			role="option"
			onMouseEnter={props.onMouseEnter}
			onMouseLeave={props.onMouseLeave}
			aria-selected={props.raised}
			layout="position"
			custom={props.slide}
			variants={SLOT_VARIANTS}
			initial={props.slide ? OFFSCREEN : false}
			animate={{
				x: 0,
				opacity: 1,
				height: height === undefined ? 'auto' : height + BORDER_WIDTH * 2,
			}}
			exit="exit"
			onAnimationComplete={props.onSettled}
			transition={{
				x: SLIDE_TRANSITION,
				opacity: INSTANT,
				layout: props.slide ? HEIGHT_TRANSITION : INSTANT,
				height: HEIGHT_TRANSITION,
			}}
		>
			<div ref={contentRef} data-slot-content>
				<AnimatePresence mode="popLayout" initial={false}>
					<motion.div
						key={props.contentKey}
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						transition={SURFACE_TRANSITION}
					>
						{props.children}
					</motion.div>
				</AnimatePresence>
			</div>
		</Slot>
	);
}
